import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, X, ShieldCheck, MessageSquare, MessageCircle, ChevronRight, Star, StarOff, KeyRound, Copy } from "lucide-react";
import useBackdropDismiss from "@/lib/useBackdropDismiss";

function waLink(phone, message) {
  if (!phone) return "";
  const cleaned = String(phone).replace(/[^0-9]/g, "");
  if (!cleaned) return "";
  return `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`;
}

export default function Admin() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [members, setMembers] = useState([]);
  const [resets, setResets] = useState([]);
  const [resetResult, setResetResult] = useState(null); // {temp_password, user}
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null); // member detail modal
  const detailBackdrop = useBackdropDismiss(() => setDetail(null));
  const resetBackdrop = useBackdropDismiss(() => setResetResult(null));

  const load = useCallback(async () => {
    setErr("");
    try {
      const [p, m, r] = await Promise.all([
        api.get("/admin/pending"),
        api.get("/admin/members"),
        api.get("/admin/password-resets"),
      ]);
      setPending(p.data);
      setMembers(m.data);
      setResets(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (u) => {
    await api.post(`/admin/approve/${u.id}`);
    // After approval, open WhatsApp pre-filled so admin can send the welcome message
    const link = waLink(
      u.phone,
      `🎉 您的 Lumière Club 申请已通过！欢迎加入 ${u.name}。请用您的注册邮箱 ${u.email} 登录：https://151mc.com\n\nYour Lumière Club application has been approved. Sign in at https://151mc.com with ${u.email}.`,
    );
    if (link) window.open(link, "_blank", "noopener");
    load();
  };
  const reject = async (uid) => {
    if (!window.confirm("Reject this application?")) return;
    await api.post(`/admin/reject/${uid}`);
    load();
  };

  const openDetail = async (uid) => {
    try {
      const r = await api.get(`/admin/users/${uid}`);
      setDetail(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const promote = async () => {
    if (!detail || !window.confirm(`Promote ${detail.name} to Concierge?`)) return;
    await api.post(`/admin/promote/${detail.id}`);
    await load();
    openDetail(detail.id);
  };

  const demote = async () => {
    if (!detail || !window.confirm(`Demote ${detail.name} back to member?`)) return;
    await api.post(`/admin/demote/${detail.id}`);
    await load();
    openDetail(detail.id);
  };

  const fulfillReset = async (req) => {
    if (!window.confirm(`Generate a temporary password for ${req.name || req.email}?`)) return;
    try {
      const r = await api.post(`/admin/password-resets/${req.id}/fulfill`);
      setResetResult(r.data);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const dismissReset = async (req) => {
    if (!window.confirm("Dismiss this request? Use this if you've verified it's fraudulent or already handled.")) return;
    try {
      await api.delete(`/admin/password-resets/${req.id}`);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const copyTempPassword = async () => {
    if (!resetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temp_password);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-center gap-3">
        <ShieldCheck size={20} strokeWidth={1.5} className="text-[#d4af37]" />
        <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">Concierge Desk</p>
      </div>
      <h1 className="font-serif text-4xl">Membership review.</h1>

      <Link
        to="/admin/dm"
        className="block border border-[#d4af37]/30 bg-[#d4af37]/5 hover:bg-[#d4af37]/10 transition rounded-md p-4 flex items-center justify-between"
        data-testid="admin-dm-link"
      >
        <div className="flex items-center gap-3">
          <MessageSquare size={18} strokeWidth={1.5} className="text-[#d4af37]" />
          <div>
            <div className="text-sm font-medium">DM Oversight</div>
            <div className="text-xs text-white/50">Read every private conversation · review violations · unmute members</div>
          </div>
        </div>
        <span className="text-[#d4af37] text-xs uppercase tracking-widest">Open →</span>
      </Link>

      <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
        {["pending", "members", "resets"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] -mb-px border-b shrink-0 ${
              tab === t ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-white/50"
            }`}
            data-testid={`admin-tab-${t}`}
          >
            {t === "pending" && `Pending · ${pending.length}`}
            {t === "members" && `Members · ${members.length}`}
            {t === "resets" && (
              <span className="flex items-center gap-1.5">
                <KeyRound size={12} strokeWidth={1.5} />
                Resets · {resets.length}
              </span>
            )}
          </button>
        ))}
      </div>
      {err && <p className="text-red-400 text-sm" data-testid="admin-error">{err}</p>}

      {tab === "pending" && (
        <div className="space-y-3" data-testid="admin-pending-list">
          {pending.length === 0 && <p className="text-white/40">No pending applications.</p>}
          {pending.map((u) => (
            <div key={u.id} className="border border-white/10 rounded-md p-5 bg-[#141414]">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="font-serif text-xl">{u.name}</div>
                  <div className="text-xs text-white/40 uppercase tracking-widest">
                    {u.occupation} · {u.region} · {u.income}
                  </div>
                  <div className="text-sm text-white/60 mt-2">
                    {u.email} · {u.phone}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(u.hobbies || []).map((h) => (
                      <span key={h} className="text-[10px] uppercase tracking-widest text-white/70 border border-white/10 px-2 py-0.5 rounded-full">
                        {h.replace("_", " ")}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => approve(u)}
                    className="bg-[#d4af37] text-black px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#f3e5ab] flex items-center gap-2"
                    data-testid={`admin-approve-${u.id}`}
                  >
                    <Check size={14} strokeWidth={2} /> Approve + WhatsApp
                  </button>
                  <a
                    href={waLink(u.phone, `您好 ${u.name}，关于您的 Lumière Club 申请...`)}
                    target="_blank"
                    rel="noreferrer"
                    className="border border-white/10 px-4 py-2 text-xs uppercase tracking-widest text-white/60 hover:border-[#25d366]/60 hover:text-[#25d366] flex items-center gap-2"
                    data-testid={`admin-whatsapp-${u.id}`}
                  >
                    <MessageCircle size={14} strokeWidth={2} /> WhatsApp
                  </a>
                  <button
                    onClick={() => reject(u.id)}
                    className="border border-white/10 px-4 py-2 text-xs uppercase tracking-widest text-white/60 hover:border-red-500/50 hover:text-red-400 flex items-center gap-2"
                    data-testid={`admin-reject-${u.id}`}
                  >
                    <X size={14} strokeWidth={2} /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "members" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-members-list">
          {members.map((u) => {
            // Prefer server-side fields (covers admin / rejected referrers correctly);
            // fall back to client-side lookups for older API versions.
            const fallbackReferrer = members.find((m) => m.id === u.referred_by);
            const referrerName = u.referrer_name
              || (fallbackReferrer ? fallbackReferrer.name : (u.referred_by ? "—" : "Direct (no referrer)"));
            const invitedCount = (typeof u.invite_count === "number")
              ? u.invite_count
              : members.filter((m) => m.referred_by === u.id).length;
            return (
              <button
                key={u.id}
                onClick={() => openDetail(u.id)}
                className="text-left border border-white/10 hover:border-[#d4af37]/40 transition rounded-md p-4 bg-[#141414] flex items-center justify-between"
                data-testid={`admin-member-${u.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium truncate">{u.name}</div>
                    {u.role === "admin" && (
                      <span className="text-[10px] uppercase tracking-widest text-[#d4af37]">Concierge</span>
                    )}
                  </div>
                  <div className="text-xs text-white/40 mt-1 truncate">
                    {u.occupation || "—"} · {u.region || "—"}
                  </div>
                  <div className="text-xs text-white/40 mt-1 flex items-center flex-wrap gap-x-3 gap-y-0.5">
                    <span>Code: <span className="font-mono text-white/70" data-testid={`admin-member-code-${u.id}`}>{u.referral_code}</span></span>
                    <span data-testid={`admin-member-referrer-${u.id}`}>
                      Referrer:&nbsp;
                      <span className="text-white/70">{referrerName}</span>
                    </span>
                    {invitedCount > 0 && (
                      <span className="text-emerald-300" data-testid={`admin-member-invited-${u.id}`}>
                        Invited · {invitedCount}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight size={16} strokeWidth={1.5} className="text-white/40 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {tab === "resets" && (
        <div className="space-y-3" data-testid="admin-resets-list">
          {resets.length === 0 && (
            <p className="text-white/40 text-sm">
              No pending password-reset requests. They auto-expire after 24h.
            </p>
          )}
          {resets.map((r) => (
            <div key={r.id} className="border border-white/10 rounded-md p-5 bg-[#141414]">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-serif text-xl">{r.name || r.email}</div>
                  <div className="text-xs text-white/40 uppercase tracking-widest mt-1">
                    {r.email}
                  </div>
                  {r.phone && (
                    <div className="text-sm text-white/60 mt-1">WhatsApp: {r.phone}</div>
                  )}
                  <div className="text-xs text-white/40 mt-2">
                    Requested {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => fulfillReset(r)}
                    className="bg-[#d4af37] text-black px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#f3e5ab] flex items-center gap-2"
                    data-testid={`admin-reset-fulfill-${r.id}`}
                  >
                    <KeyRound size={14} strokeWidth={2} /> Generate temp password
                  </button>
                  <button
                    onClick={() => dismissReset(r)}
                    className="border border-white/10 px-4 py-2 text-xs uppercase tracking-widest text-white/60 hover:border-red-500/50 hover:text-red-400 flex items-center gap-2"
                    data-testid={`admin-reset-dismiss-${r.id}`}
                  >
                    <X size={14} strokeWidth={2} /> Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Member detail modal */}
      {detail && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
          {...detailBackdrop}
          data-testid="admin-detail-modal"
        >
          <div className="w-full max-w-xl bg-[#0a0a0a] border border-white/10 rounded-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-[#d4af37] text-[10px] uppercase tracking-[0.3em]">Member Detail</div>
                <div className="font-serif text-2xl mt-1">{detail.name}</div>
                <div className="text-xs text-white/50 mt-0.5">
                  {detail.role === "admin" ? "Concierge" : "Member"} · joined {detail.created_at?.slice(0, 10)}
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="text-white/50 hover:text-white p-1" data-testid="admin-detail-close">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <Row k="Email" v={detail.email} />
              <Row k="WhatsApp" v={detail.phone} extra={
                detail.phone && (
                  <a
                    href={waLink(detail.phone, `Hi ${detail.name}, from Lumière Club Concierge.`)}
                    target="_blank" rel="noreferrer"
                    className="text-[#25d366] text-xs uppercase tracking-widest flex items-center gap-1"
                    data-testid="admin-detail-whatsapp"
                  >
                    <MessageCircle size={12} strokeWidth={1.5} /> Open
                  </a>
                )
              } />
              <Row k="Region" v={detail.region} />
              <Row k="Occupation" v={detail.occupation} />
              <Row k="Income" v={detail.income} />
              <Row k="Hobbies" v={(detail.hobbies || []).join(", ")} />
              <Row k="Referrer" v={detail.referrer_name || "—"} />
              <Row k="Status" v={detail.status} />
              <Row k="Stories" v={detail.story_count} />
              <Row k="Friends" v={detail.friend_count} />
              <Row k="Groups" v={(detail.groups || []).map((g) => g.name).join(", ") || "None"} />
              {detail.chat_banned_until && (
                <Row k="Muted until" v={<span className="text-red-300">{detail.chat_banned_until}</span>} />
              )}
            </div>
            <div className="p-5 border-t border-white/10 flex flex-wrap gap-3">
              {detail.id === currentUser?.id ? (
                <p className="text-xs text-white/50 italic">You cannot change your own role from here.</p>
              ) : detail.role !== "admin" ? (
                <button
                  onClick={promote}
                  className="flex items-center gap-2 bg-[#d4af37] text-black px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#f3e5ab]"
                  data-testid="admin-promote"
                >
                  <Star size={14} strokeWidth={2} /> Promote to Concierge
                </button>
              ) : (
                <button
                  onClick={demote}
                  className="flex items-center gap-2 border border-white/10 hover:border-red-400/50 hover:text-red-300 px-4 py-2 text-xs uppercase tracking-widest text-white/70"
                  data-testid="admin-demote"
                >
                  <StarOff size={14} strokeWidth={2} /> Demote to Member
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* One-shot temp-password reveal */}
      {resetResult && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          {...resetBackdrop}
          data-testid="admin-reset-result-modal"
        >
          <div className="w-full max-w-md bg-[#0a0a0a] border border-[#d4af37]/40 rounded-lg">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-[#d4af37] text-[10px] uppercase tracking-[0.3em]">Temporary password</div>
                <div className="font-serif text-2xl mt-1">{resetResult.user.name || resetResult.user.email}</div>
              </div>
              <button
                onClick={() => setResetResult(null)}
                className="text-white/50 hover:text-white p-1"
                data-testid="admin-reset-result-close"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-white/60 leading-relaxed">
                Share this temporary password with the member <strong>now</strong>.
                You will not see it again. The member will be required to set a new
                password the first time they sign in.
              </p>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 font-mono text-lg bg-black px-4 py-3 border border-white/10 rounded select-all break-all"
                  data-testid="admin-reset-temp-password"
                >
                  {resetResult.temp_password}
                </code>
                <button
                  onClick={copyTempPassword}
                  className="border border-white/10 px-3 py-3 hover:border-[#d4af37]/40"
                  data-testid="admin-reset-copy"
                  title="Copy"
                >
                  <Copy size={14} strokeWidth={1.5} />
                </button>
              </div>
              {resetResult.user.phone && (
                <a
                  href={waLink(
                    resetResult.user.phone,
                    `Hi ${resetResult.user.name}, this is Lumière Concierge.\n\nYour temporary password is:\n${resetResult.temp_password}\n\nPlease sign in at https://151mc.com — you will be asked to set a new password.\n\n您的临时密码：${resetResult.temp_password}\n登录后请立即修改密码。`,
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full bg-[#25d366] text-black px-4 py-3 text-xs uppercase tracking-widest font-medium hover:bg-[#36e077] flex items-center justify-center gap-2"
                  data-testid="admin-reset-whatsapp"
                >
                  <MessageCircle size={14} strokeWidth={2} /> Send via WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, extra }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-[10px] uppercase tracking-widest text-white/40 w-24 shrink-0">{k}</div>
      <div className="flex-1 text-white/85 break-all">{v ?? "—"}</div>
      {extra}
    </div>
  );
}
